import { Popover, Skeleton } from "antd";
import { useState } from "react";
import { StatusTag } from "../../../components/atoms/Tag";
import {
  useGetSaleInvoiceIssuesQuery,
  useGetSaleInvoiceLineIssuesQuery,
} from "../../../store/apiSlice/invoiceApiSlice";
import {
  IssueGroup,
  IssueGroupTitle,
  IssueItem,
  IssueItemMessage,
  IssueItemTitle,
  IssueList,
  IssuePopover,
} from "./IssueTags.styles";

/** @const */
const TYPE_ORDER = ["error", "warning", "processed"];

/** @const */
const TYPE_LABELS = {
  error: "Error",
  warning: "Warning",
  processed: "Processed",
};

/** @const */
const CODE_LABELS = {
  FULFILLMENT_FAILED: "Fulfillment Failed",
  FULFILLMENT_PROCESSED: "Processed",
  INSUFFICIENT_STOCK: "Insufficient Stock",
  MISSING_PACK_LOCATION: "Missing Pack Location",
  MISSING_PACKAGING_TYPE: "Missing Packaging Type",
  XERO_APPROVED_SHORT_STOCK: "Xero Short Stock",
};

/**
 * Human-readable Error / Warning label for an issue type.
 *
 * @param {string=} type
 * @return {string}
 */
const typeLabel = type => TYPE_LABELS[type] || TYPE_LABELS.error;

/**
 * Unique issue types in display order (error, then warning).
 *
 * @param {!Array<{type: (string|undefined)}>} tags
 * @return {!Array<string>}
 */
const uniqueTypes = tags =>
  TYPE_ORDER.filter(type => tags.some(tag => (tag.type || "error") === type));

/**
 * Title for an issue code, e.g. Insufficient Stock.
 *
 * @param {string=} code
 * @return {string}
 */
const humanizeCode = code =>
  CODE_LABELS[code] ||
  String(code || "")
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, char => char.toUpperCase());

/**
 * Group tags under Error then Warning, preserving item order.
 *
 * @param {!Array<Object>} tags
 * @return {!Array<{type: string, items: !Array<Object>}>}
 */
const groupTagsByType = tags =>
  TYPE_ORDER.map(type => ({
    type,
    items: tags.filter(tag => (tag.type || "error") === type),
  })).filter(group => group.items.length);

const IssuePopoverBody = ({ items, isFetching, isError, waiting }) => {
  if ((waiting || isFetching) && !items?.length) {
    return (
      <IssuePopover>
        <Skeleton active title={false} paragraph={{ rows: 3, width: ["90%", "80%", "70%"] }} />
      </IssuePopover>
    );
  }
  if (isError) {
    return <IssuePopover>Could not load fulfillment status.</IssuePopover>;
  }
  if (!items?.length) {
    return <IssuePopover>No fulfillment status.</IssuePopover>;
  }
  const groups = groupTagsByType(items);
  return (
    <IssuePopover>
      {groups.map(group => (
        <IssueGroup key={group.type}>
          <IssueGroupTitle $type={group.type}>{typeLabel(group.type)}</IssueGroupTitle>
          <IssueList>
            {group.items.map((tag, index) => (
              <IssueItem key={`${tag.code}-${index}`}>
                <IssueItemTitle>{humanizeCode(tag.code)}:</IssueItemTitle>{" "}
                {tag.message ? <IssueItemMessage>{tag.message}</IssueItemMessage> : null}
              </IssueItem>
            ))}
          </IssueList>
        </IssueGroup>
      ))}
    </IssuePopover>
  );
};

/**
 * Issue tag pills. Hovering the cell fetches titles and messages once, then
 * caches them on that invoice or line.
 *
 * @param {Object} props
 * @param {Array<{code?: string, type?: string, message?: string}>} [props.tags]
 * @param {number} [props.width]
 * @param {number} [props.invoiceId]
 * @param {number} [props.lineId]
 * @returns {JSX.Element}
 */
export const IssueTags = ({ tags, width, invoiceId, lineId }) => {
  const [loadMessages, setLoadMessages] = useState(false);

  const invoiceQuery = useGetSaleInvoiceIssuesQuery(invoiceId, {
    skip: !invoiceId || !loadMessages,
  });
  const lineQuery = useGetSaleInvoiceLineIssuesQuery(lineId, {
    skip: !lineId || !loadMessages,
  });
  const query = lineId ? lineQuery : invoiceQuery;

  if (!tags?.length) {
    return null;
  }

  const pillTypes = uniqueTypes(tags);
  const loadedItems = query.data?.items;

  return (
    <Popover
      trigger={["hover", "click"]}
      placement="bottomLeft"
      overlayInnerStyle={{ padding: 12 }}
      onOpenChange={visible => {
        if (visible) setLoadMessages(true);
      }}
      content={
        <IssuePopoverBody
          items={loadedItems}
          isFetching={query.isFetching}
          isError={query.isError}
          waiting={loadMessages && !loadedItems}
        />
      }
    >
      <span
        className="issue-tags"
        style={{
          cursor: "default",
          display: "block",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          width: width != null ? width : "100%",
          maxWidth: "100%",
        }}
      >
        {pillTypes.map(type => (
          <span key={type} style={{ display: "inline-flex", marginRight: 8 }}>
            <StatusTag className={typeLabel(type)}>{typeLabel(type)}</StatusTag>
          </span>
        ))}
      </span>
    </Popover>
  );
};

export const issueTagsFromLine = line => {
  const items = line?.issues?.items;
  if (!Array.isArray(items) || !items.length) return [];
  return items;
};

/**
 * Unique Error / Warning / Processed labels for a line filter.
 *
 * @param {Object=} line
 * @return {!Array<string>}
 */
export const issueTypeLabelsFromLine = line =>
  uniqueTypes(issueTagsFromLine(line)).map(typeLabel);
